<template>
  <div class="voice-bg">
    <div class="captions-switch" @click="captionsSwitch = !captionsSwitch">
      <span v-if="captionsSwitch">关闭字幕</span>
      <span v-else>字幕</span>
    </div>
    <div class="voice-animation">
      <div class="leaf" :style="{ animationPlayState: generating ? 'running' : 'paused' }">
        <i v-for="item in 3" :key="`leaf-${item}`" />
      </div>
      <div class="leaves" :style="{ animationPlayState: generating ? 'running' : 'paused' }">
        <i v-for="item in 3" :key="`leaves-${item}`" />
      </div>
      <div class="stamens">
        <i />
      </div>
    </div>
    <div v-if="captionsSwitch && currentCaptions" class="captions">{{ currentCaptions }}</div>
    <div v-if="!hasPermission" class="get-permission">
      <span>授权麦克风，开启语音对话</span>
      <div class="permission-btn" @click="openRecorder"><i class="el-icon-microphone" />开启麦克风</div>
    </div>
    <div class="voice-operation">
      <div ref="voiceWave" class="voice-wave" style="width: 100%; height: 40px;" />
      <div class="operation-btn">
        <i v-if="hasPermission && !micphoneOff" class="el-icon-microphone" @click="switchMicphone" />
        <i v-else class="el-icon-turn-off-microphone red-btn" @click="startRecord" />
        <i v-if="generating" class="stop-icon" @click="stopGenerate" />
        <i v-else class="el-icon-close red-btn" @click="backToChats" />
      </div>
    </div>
    <audio ref="audio" :src="currentVoice" preload="auto" />
  </div>
</template>

<script>
import { getChatInfo } from '@/api/customer';
import Recorder from 'recorder-core';
import 'recorder-core/src/engine/mp3';
import 'recorder-core/src/engine/mp3-engine';
import 'recorder-core/src/extensions/waveview';
export default {
  data() {
    return {
      agentId: '',
      chatInfo: {},
      hasPermission: false,
      voiceInstance: null,
      voiceTimer: null,
      wave: null,
      voiceDuration: 0,
      audioContext: null,
      mediaStream: null,
      generating: false,
      micphoneOff: false,
      currentQuestion: '',
      voiceList: [],
      currentVoice: '',
      captionsSwitch: false,
      captionsList: [],
      currentCaptions: '',
    }
  },
  watch: {
    generating(val) {
      if (!val) {
        this.startRecord();
      }
    },
    voiceList: {
      deep: true,
      handler(val) {
        if (this.currentVoice === '') {
          // 播放语音
          this.playVoiceList();
        } else {
          // 预加载下一条语音
          if (val.length > 1) {
            const audio = document.createElement('audio');
            audio.src = val[0];
            audio.preload = 'auto';
            console.log('预加载下一条语音', audio.src);
            audio.load();
          }
        }
      }
    }
  },
  mounted() {
    document.title = '智能小助手';
    this.agentId = this.$route.params.id;
    if (!this.agentId) {
      this.$router.replace('/404')
    }
    this.getChatInfo();
    this.$refs.audio.addEventListener('ended', this.playVoiceList);
    window.addEventListener('resize', this.resize);
    this.resize();
    this.initRecorder();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize);
    this.$refs.audio.removeEventListener('ended', this.playVoiceList);
    this.voiceInstance && this.voiceInstance.close();
  },
  methods: {
    // 获取机器人信息
    async getChatInfo() {
      const { data } = await getChatInfo({ agent_id: this.agentId });
      if (data.chat_id) {
        document.title = data.name;
        this.chatInfo = data;
      } else {
        this.$router.replace('/404')
      }
    },
    // 构造录音实例
    initRecorder() {
      this.voiceInstance = Recorder({
        type: 'mp3',
        sampleRate: 16000,
        bitRate: 16,
        onProcess: (buffers, powerLevel, bufferDuration, bufferSampleRate) => {
          this.voiceDuration = bufferDuration;
          this.wave.input(buffers[buffers.length - 1], powerLevel, bufferSampleRate);
          if (powerLevel > 10) {
            console.log('powerLevel', this.voiceTimer);
            if (this.voiceTimer) {
              clearTimeout(this.voiceTimer);
            }
            this.voiceTimer = setTimeout(() => {
              this.stopRecord();
              clearTimeout(this.voiceTimer);
            }, 2500);
          } else {
            console.log('noPowerLevel', this.voiceTimer);
          }
        }
      });
    },
    openRecorder() {
      this.voiceInstance.open(() => {
        setTimeout(() => {
          this.hasPermission = true;
          this.voiceInstance.close();
          console.log(this.voiceInstance);
          this.startRecord();
        }, 100);
      }, (msg, isUserNotAllow) => {
        if (isUserNotAllow) {
          this.$message.error('用户拒绝使用麦克风权限');
        } else {
          this.$message.error(msg);
        }
      });
      return;
    },
    async startRecord() {
      this.voiceInstance.open(() => {
        this.wave = Recorder.WaveView({ elem: this.$refs.voiceWave });
        this.voiceInstance.start();
      }, (msg, isUserNotAllow) => {
        if (isUserNotAllow) {
          this.$message.error('用户拒绝使用麦克风权限');
        } else {
          this.$message.error(msg);
        }
      });
    },
    stopRecord() {
      console.log('录音时长：' + this.voiceDuration);
      this.voiceInstance.stop(async blob => {
        if (this.voiceDuration < 1000) {
          return;
        }
        this.generating = true;
        const formData = new FormData();
        formData.append('file', blob, 'voice.mp3');
        const response = await fetch(process.env.VUE_APP_VOICE2TEXT_API, {
          method: 'POST',
          body: formData
        })
        if (!response.ok) {
          this.$message.error('服务繁忙，请稍候重试');
          this.generating = false;
          return;
        }
        const reader = response.body.getReader();
        const { value } = await reader.read();
        const textDecoder = new TextDecoder('utf-8');
        const result = JSON.parse(textDecoder.decode(value));
        if (!result.data.text || result.data.text.trim().length < 1) {
          this.$message.warning('未识别到语音内容');
          this.generating = false;
          return;
        }
        this.currentQuestion = result.data.text;
        this.sendMessage(result.data.text);
      }, (err) => {
        this.generating = false;
        console.log('录音错误：' + err);
      });
    },
    async sendMessage(content) {
      const response = await fetch(process.env.VUE_APP_AI_CHAT_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        responseType: 'stream',
        body: JSON.stringify({
          agent_id: this.agentId,
          chat_id: this.chatInfo.chat_id,
          prompt: content,
          search: 0
        })
      });
      if (!response.ok) {
        this.generating = false;
        return;
      }
      const reader = response.body.getReader();
      const textDecoder = new TextDecoder('utf-8');
      let result = true;
      while (result) {
        const { done, value } = await reader.read();
        if (done || this.stopFlag) {
          result = false;
          break;
        }
        textDecoder.decode(value).split('\n').forEach(item => {
          if (!item) {
            return;
          }
          const resultStr = item?.replace("data:", '').replaceAll(' ', ' ') || '';
          const resultObj = JSON.parse(resultStr);
          // 讲声音源插入音频列表中
          if (resultObj.voice) {
            this.voiceList.push(resultObj.voice);
            this.captionsList.push(resultObj.answer);
          }
        })
      }
    },
    async stopGenerate() {
      await fetch(process.env.VUE_APP_STOP_CHAT_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chat_id: this.chatInfo.chat_id,
          prompt: this.currentQuestion,
        })
      });
      this.currentQuestion = '';
      this.voiceList = [];
      this.currentVoice = '';
      this.captionsList = [];
      this.currentCaptions = '';
      this.generating = false;
    },
    playVoiceList() {
      this.currentVoice = '';
      this.currentCaptions = '';
      if (this.voiceList.length > 0) {
        this.currentVoice = this.voiceList[0];
        this.voiceList.shift();
        this.currentCaptions = this.captionsList[0];
        this.captionsList.shift();
        this.$nextTick(() => {
          this.$refs.audio.play();
        });
      } else {
        this.generating = false;
      }
    },
    switchMicphone() {
      this.micphoneOff = !this.micphoneOff;
      if (this.micphoneOff) {
        this.voiceInstance.close();
      } else {
        this.startRecord();
      }
    },
    resize(e) {
      const target = e ? e.target : window;
      if (target.innerWidth > 768) {
        this.backToChats();
      }
      if (this.wave) {
        this.wave.resize();
      }
    },
    backToChats() {
      this.$router.replace(`/robot/${this.agentId}`);
    }
  }
}
</script>

<style scoped lang="less">
.voice-bg {
  width: 100vw;
  min-height: 100vh;
  position: relative;
  background: radial-gradient(circle at 80% 20%, #B2E9E9, transparent 50%),
              radial-gradient(circle at 40% 70%, #EAE8FF, transparent 60%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 160px;

  .captions-switch {
    position: absolute;
    top: 20px;
    right: 20px;
    padding: 8px 16px;
    border-radius: 20px;
    background-color: #FFFFFF;
    font-size: 14px;
  }

  .voice-animation {
    width: 240px;
    height: 240px;
    position: relative;

    .leaf {
      width: 205px;
      height: 205px;
      position: absolute;
      top: calc(50% - 102.5px);
      left: calc(50% - 102.5px);
      animation: clockwise 8s linear infinite;

      i {
        width: 205px;
        height: 110px;
        display: block;
        clip-path: ellipse(50% 50% at 50% 50%);
        background: linear-gradient(to right, rgba(114, 240, 250, 0.1), rgba(184, 167, 255, 0.1));
        position: absolute;
        top: calc(50% - 55px);
        left: calc(50% - 102.5px);

        &:nth-child(1) {
          transform: rotate(0deg);
        }

        &:nth-child(2) {
          transform: rotate(60deg);
        }

        &:nth-child(3) {
          transform: rotate(120deg);
        }
      }
    }

    .leaves {
      width: 150px;
      height: 150px;
      position: absolute;
      top: calc(50% - 75px);
      left: calc(50% - 75px);
      transform: rotate(30deg);
      animation: counterclockwise 8s linear infinite;

      i {
        width: 150px;
        height: 80px;
        display: block;
        clip-path: ellipse(50% 50% at 50% 50%);
        background: linear-gradient(to right, rgba(114, 240, 250, 0.2), rgba(184, 167, 255, 0.2));
        position: absolute;
        top: calc(50% - 40px);
        left: calc(50% - 75px);
        overflow: hidden;

        &:nth-child(1) {
          transform: rotate(0deg);
        }

        &:nth-child(2) {
          transform: rotate(60deg);
        }

        &:nth-child(3) {
          transform: rotate(120deg);
        }
      }
    }

    .stamens {
      width: 86px;
      height: 86px;
      border-radius: 43px;
      position: absolute;
      top: calc(50% - 43px);
      left: calc(50% - 43px);
      background: linear-gradient(to bottom, rgba(94, 204, 244, 0.6), rgba(158, 120, 245, 0.6));

      i {
        width: 40px;
        height: 40px;
        border-radius: 20px;
        background: linear-gradient(to bottom, rgba(94, 141, 244, 0.4), rgba(187, 120, 245, 0.4));
        position: absolute;
        top: calc(50% - 20px);
        left: calc(50% - 20px);
      }
    }
  }

  .captions {
    position: absolute;
    left: 0;
    bottom: 200px;
    width: 100vw;
    text-align: center;
    font-size: 16px;
    padding: 8px 0;
    font-size: 14px;
    color: #363636;
    padding: 0 32px;
  }

  .get-permission {
    width: 100%;
    position: absolute;
    bottom: 160px;
    left: 0;
    padding: 0 64px;
    font-size: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;

    .permission-btn {
      width: 100%;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      background-color: #FFFFFF;
      border-radius: 8px;

      i {
        font-size: 20px;
      }
    }
  }

  .voice-operation {
    width: 100%;
    position: fixed;
    bottom: 0;
    left: 0;
    padding-bottom: env(safe-area-inset-bottom);
    padding-bottom: constant(safe-area-inset-bottom);

    .operation-btn {
      width: 100%;
      padding: 32px 64px;
      display: flex;
      align-items: center;
      justify-content: space-around;

      i {
        width: 60px;
        height: 60px;
        border-radius: 30px;
        background-color: #FFFFFF;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 28px;

        &.stop-icon{
          &::before{
            content: "";
            position: absolute;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            border: 2px solid var(--primary-text-color);
          }
          &::after{
            content: "";
            position: absolute;
            width: 12px;
            height: 12px;
            background-color: var(--primary-text-color);
          }
        }

        &.red-btn {
          color: #FF4949;
        }
      }
    }
  }
}

@keyframes clockwise {
  0% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(180deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
@keyframes counterclockwise {
  0% {
    transform: rotate(30deg);
  }
  50% {
    transform: rotate(-160deg);
  }
  100% {
    transform: rotate(-330deg);
  }
}
</style>
